'use client';

import type React from 'react';
import { useState, useEffect, useMemo } from 'react';
import {
  Popover,
  Button,
  List,
  ListItemText,
  Typography,
  Box,
  IconButton,
  ListItemButton,
  InputLabel,
} from '@mui/material';
import { FiPlus } from '@react-icons/all-files/fi/FiPlus';
import { MdArrowBack } from '@react-icons/all-files/md/MdArrowBack';

import { SearchField } from '../input-search';
import { HiOutlineChevronDown } from '@react-icons/all-files/hi/HiOutlineChevronDown';
import { HiOutlineChevronUp } from '@react-icons/all-files/hi/HiOutlineChevronUp';
import { FaRegTrashAlt } from '@react-icons/all-files/fa/FaRegTrashAlt';
import { Loading } from '../../../loading';
import { InputText } from '../input-text';
import useSWR from 'swr';
import { useSnackbar } from '../../../snackbar';
import { SelectCreatorManager } from './select-creator-manager';

interface Category {
  name: string;
  id: string;
  canEdit: boolean;
}

interface CategorySelectorProps {
  requestId: string;
  onSelect: (category: Category) => void;
  categoryValue?: string;
  disabled?: boolean;
  name: string;
  label?: string;
  categoryManager: SelectCreatorManager;
}

export function SelectorCreator({
  requestId,
  onSelect,
  categoryValue,
  disabled = false,
  name,
  label,
  categoryManager,
}: CategorySelectorProps) {
  const { data: categories = [], mutate } = useSWR(
    [requestId, categoryManager.teamId, 'selector-creator'],
    () => categoryManager.get()
  );

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [search, setSearch] = useState<string | null>(null);
  const [showNewCategoryForm, setShowNewCategoryForm] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [currentCategory, setCurrentCategory] = useState<Category | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<string | null>(null);
  const { openSnackbar } = useSnackbar();

  const filteredCategories = useMemo(() => {
    if (!search) return categories;
    return categories.filter((category) =>
      category.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, categories]);

  useEffect(() => {
    if (categoryValue) {
      const foundedCategory = categories.find(
        (category) => category.name === categoryValue
      );
      setCurrentCategory(foundedCategory ?? null);
    }
  }, [categoryValue, categories]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setShowNewCategoryForm(false);
    setNewCategoryName('');
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSearchChange = (term: string) => {
    setSearch(term);
  };

  const handleSelectCategory = (category: Category) => {
    onSelect(category);
    setCurrentCategory(category);
    handleClose();
  };

  const handleShowNewCategoryForm = () => {
    setShowNewCategoryForm(true);
  };

  const handleBackToList = () => {
    setShowNewCategoryForm(false);
    setNewCategoryName('');
  };

  const handleNewCategoryNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setNewCategoryName(event.target.value);
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;

    setIsCreating(true);
    try {
      const newCategory = await categoryManager.create(newCategoryName);
      await mutate((prev) => [...(prev || []), newCategory], false);
      onSelect(newCategory);
      handleClose();
    } catch (error) {
      console.error('Erro ao criar categoria:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    setDeletingCategory(categoryId);
    try {
      await categoryManager.delete(categoryId);
      await mutate(
        (prev) => prev?.filter((category) => category.id !== categoryId),
        false
      );
    } catch (error) {
      console.error('Erro ao deletar categoria:', error);
      openSnackbar({
        message: `Erro ao criar categoria: ${error}`,
        type: 'error',
      });
    } finally {
      setDeletingCategory(null);
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? 'category-popover' : undefined;

  return (
    <div>
      {label && <InputLabel>{label}</InputLabel>}
      <Button
        variant="outlined"
        color="secondary"
        disableFocusRipple
        fullWidth
        onClick={handleClick}
        disabled={disabled}
        sx={{
          borderRadius: 1.5,
          fontWeight: 'normal',
          border: '1px solid #E3E3E3',
          color: !currentCategory ? '#959b9e' : 'text.primary',
          justifyContent: 'space-between',
        }}
        endIcon={
          open ? (
            <HiOutlineChevronUp size={16} />
          ) : (
            <HiOutlineChevronDown size={16} />
          )
        }
      >
        {currentCategory?.name ?? `Mudar ${name}`}
      </Button>
      <Popover
        sx={{
          maxHeight: 500,
        }}
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            width: 320,
            maxWidth: '100%',
            borderRadius: 2,
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        {!showNewCategoryForm ? (
          <Box py={1}>
            <Box
              px={2}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography variant="h6" component="h2">
                {name}
              </Typography>
              <Button
                startIcon={<FiPlus />}
                onClick={handleShowNewCategoryForm}
              >
                Nova {name}
              </Button>
            </Box>

            <Box px={2}>
              <SearchField handleSearch={handleSearchChange} />
            </Box>

            <List disablePadding sx={{ mt: 2 }}>
              {filteredCategories.map((category) => (
                <ListItemButton
                  key={category.id}
                  onClick={() => handleSelectCategory(category)}
                  disabled={category.id === deletingCategory}
                  sx={{ py: 0.5 }}
                >
                  <ListItemText primary={category.name} />
                  {category.canEdit && (
                    <>
                      {category.id === deletingCategory ? (
                        <Loading size={16} />
                      ) : (
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCategory(category.id);
                          }}
                        >
                          <FaRegTrashAlt size={16} />
                        </IconButton>
                      )}
                    </>
                  )}
                </ListItemButton>
              ))}
            </List>
          </Box>
        ) : (
          <Box p={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <IconButton onClick={handleBackToList} sx={{ mr: 1 }}>
                <MdArrowBack />
              </IconButton>
              <Typography variant="h6" component="h2">
                Nova {name}
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Estratégias criadas poderão ser usadas por todos em seu time.
            </Typography>

            <InputText
              placeholder={`Nome da ${name}`}
              variant="outlined"
              size="small"
              fullWidth
              value={newCategoryName}
              onChange={handleNewCategoryNameChange}
              sx={{ mb: 2 }}
              autoFocus
            />

            <Button
              variant="contained"
              fullWidth
              onClick={handleCreateCategory}
              disabled={!newCategoryName.trim() || isCreating}
              sx={{
                bgcolor: 'green',
                color: 'white',
                textTransform: 'none',
                py: 1.5,
                '&:hover': {
                  bgcolor: 'darkgreen',
                },
                '&.Mui-disabled': {
                  bgcolor: 'rgba(0, 128, 0, 0.5)',
                  color: 'white',
                },
              }}
            >
              {isCreating ? 'Criando...' : 'Aplicar'}
            </Button>
          </Box>
        )}
      </Popover>
    </div>
  );
}
